import { UpgradeLoggerService } from 'ngx-core-services';

import { SgpInteraction } from './sgp-interaction.model';

/**
 * Enhanced SgpInteraction that logs raw SGP commands and responses
 */
export class SgpInteractionWithLogging extends SgpInteraction {
  private commandCounter = 0;

  constructor(
    execute: (cmd: Uint8Array) => Promise<Uint8Array>,
    slaveId: number,
    private readonly upgradeLogger: UpgradeLoggerService,
    private readonly moduleId?: string,
    deviceId?: string,
  ) {
    super(execute, slaveId, deviceId);
  }

  /**
   * Override the interaction method to capture raw commands and responses
   */
  async interaction(cmd: Uint8Array, MAX_TIMEOUT_IN_MS = 30000): Promise<Uint8Array> {
    const commandId = ++this.commandCounter;
    const rawCommand = this.bytesToHex(cmd);

    console.log(`🔍 SGP Raw Command ${commandId} (${cmd.length} bytes):`, rawCommand);

    try {
      // Call parent implementation
      const response = await super.interaction(cmd, MAX_TIMEOUT_IN_MS);
      const rawResponse = this.bytesToHex(response);

      console.log(`🔍 SGP Raw Response ${commandId} (${response.length} bytes):`, rawResponse);

      // Log to Firestore if logger available
      if (this.upgradeLogger) {
        await this.logSgpCommand(commandId, rawCommand, rawResponse, true);
      }

      return response;
    } catch (error) {
      console.error(`❌ SGP Command ${commandId} failed:`, error);

      // Log error to Firestore if logger available
      if (this.upgradeLogger) {
        await this.logSgpCommand(commandId, rawCommand, null, false, error);
      }

      throw error;
    }
  }

  /**
   * Log SGP command to Firestore
   */
  private async logSgpCommand(
    commandId: number,
    rawCommand: string,
    rawResponse: string | null,
    success: boolean,
    error?: any,
  ): Promise<void> {
    try {
      const commandIdHex = commandId.toString(16).padStart(4, '0');
      await this.upgradeLogger?.logEvent({
        operation: 'sgp_command',
        phase: `Command ${commandIdHex}`,
        moduleId: this.moduleId || 'unknown',
        sgpCommandDetails: {
          commandCode: `CMD_${commandIdHex}`,
          commandName: 'Raw SGP Command',
          rawCommand,
          rawResponse: rawResponse || undefined,
          success,
        },
      });

      console.log(`✅ SGP Command ${commandIdHex} logged to Firestore`);
    } catch (logError) {
      console.error('Failed to log SGP command to Firestore:', logError);
    }
  }

  /**
   * Convert bytes to hex string
   */
  private bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join(' ');
  }
}
